// MapDetailPanel.tsx -- Right-side node detail overlay.
// Shows conversation details: title, topic, site, project, time.
// Skin: dashboard-overhaul warm tokens (--panel/--hair/--hair2/--ink/--inkB/--inkS/--faint).

import { useEffect, useRef, useState, useCallback } from 'react';
import { siteColor, siteLabel } from '../../lib/sites';
import type { GraphNode } from './types';

interface MapDetailPanelProps {
  nodeId: string;
  nodes: GraphNode[];
  onClose: () => void;
  // Resolves on a confirmed server delete, rejects otherwise. This used to be fire-and-forget: the
  // panel closed on click whatever happened and the only failure channel was a console.error — so
  // deleting a TEAMMATE's node (the server scopes deletes to the caller's own conversations, and
  // this button renders on every node of a team-wide map) looked exactly like success.
  onDelete: (id: string) => Promise<void>;
  onNav: (id: string) => void;
}

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

const LABEL_STYLE: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace", fontSize: 9, color: 'var(--faint)',
  textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 4,
};

export function MapDetailPanel({ nodeId, nodes, onClose, onDelete, onNav }: MapDetailPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const node = nodes.find(n => n.id === nodeId);

  // Close on Escape key
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Close on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    // Delay to avoid closing immediately from the right-click that opened it
    const timer = setTimeout(() => {
      window.addEventListener('click', handleClick);
    }, 100);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('click', handleClick);
    };
  }, [onClose]);

  const handleDelete = useCallback(async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await onDelete(nodeId);
      onClose();
    } catch {
      // Stay open and say so — closing on a rejected delete told the user it worked.
      setDeleteError("Couldn't delete this. You can only delete your own conversations.");
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }, [confirmDelete, nodeId, onDelete, onClose]);

  const panelStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 320,
    maxWidth: '88%',
    height: '100%',
    background: 'var(--panel)',
    borderLeft: '1px solid var(--hair2)',
    padding: 24,
    zIndex: 20,
    overflowY: 'auto',
    animation: 'slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
  };

  if (!node) {
    return (
      <div ref={panelRef} role="complementary" aria-label="Node details" style={panelStyle}>
        <button
          onClick={onClose}
          aria-label="Close detail panel"
          style={{
            position: 'absolute', top: 16, right: 16, background: 'none',
            border: 'none', color: 'var(--inkS)', cursor: 'pointer', fontSize: 18, lineHeight: 1,
          }}
        >
          ×
        </button>
        <p style={{ color: 'var(--inkS)', fontSize: 14 }}>Node not found</p>
      </div>
    );
  }

  const color = siteColor(node.site);

  return (
    <div ref={panelRef} role="complementary" aria-label="Node details" style={panelStyle}>
      {/* Close button */}
      <button
        onClick={onClose}
        aria-label="Close detail panel"
        style={{
          position: 'absolute', top: 16, right: 16, background: 'none',
          border: 'none', color: 'var(--inkS)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 4,
        }}
      >
        ×
      </button>

      {/* Title */}
      <h2 style={{
        fontSize: 18, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
        color: 'var(--inkB)', marginBottom: 12, marginTop: 0, paddingRight: 24, lineHeight: 1.3,
      }}>
        {node.label}
      </h2>

      {/* Site badge */}
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '2px 10px', borderRadius: 12,
        border: `1px solid ${color}`, background: `${color}15`,
        fontFamily: "'DM Mono', monospace", fontSize: 9, fontWeight: 700,
        letterSpacing: '0.06em', textTransform: 'uppercase', color: color,
        marginBottom: 20,
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
        {siteLabel(node.site)}
      </div>

      {/* Node metadata */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <div style={LABEL_STYLE}>Topic</div>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: 'var(--ink)' }}>
            {node.topic}
          </div>
        </div>

        {node.project_name && (
          <div>
            <div style={LABEL_STYLE}>Project</div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: 'var(--ink)' }}>
              {node.project_name}
            </div>
          </div>
        )}

        <div>
          <div style={LABEL_STYLE}>Last Captured</div>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: 'var(--inkS)' }}>
            {relativeTime(node.last_captured_at)}
          </div>
        </div>

        {node.author_name && (
          <div>
            <div style={LABEL_STYLE}>Author</div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: 'var(--ink)' }}>
              {node.author_name}
              {node.author_role && (
                <span style={{ color: 'var(--faint)', marginLeft: 6 }}>· {node.author_role}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Full conversation — open the complete record (briefing deep-link route) */}
      <button
        className="btn btn-primary"
        style={{ width: '100%', justifyContent: 'center', marginTop: 20 }}
        onClick={() => { onNav('conversations/' + nodeId); onClose(); }}
      >
        View full conversation <span className="arr">→</span>
      </button>

      {/* Ask about this node — seed the /ask composer (blueprint steps 20-21). onNav('ask?q=…') mirrors
          the map's own '?highlight=' query-nav; ?q prefills the composer and never auto-submits. */}
      <button
        className="btn btn-secondary"
        style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}
        onClick={() => { onNav('ask?q=' + encodeURIComponent(`Tell me about ${node.topic || node.label}`)); onClose(); }}
      >
        <span aria-hidden="true">◈</span> Ask about this
      </button>

      {/* Separator */}
      <div style={{ borderTop: '1px solid var(--hair)', margin: '24px 0' }} />

      {deleteError && (
        <p style={{ fontSize: 12, color: 'var(--rose)', margin: '0 0 10px', lineHeight: 1.45 }} role="alert">
          {deleteError}
        </p>
      )}

      {/* Delete action with inline confirmation */}
      {!confirmDelete ? (
        <button
          onClick={handleDelete}
          style={{
            background: 'none', border: '1px solid var(--hair2)', borderRadius: 8,
            padding: '9px 16px', color: 'var(--faint)', fontFamily: "'DM Sans', sans-serif",
            fontSize: 13, cursor: 'pointer', width: '100%',
            transition: 'border-color 0.15s, color 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--rose) 45%, transparent)'; e.currentTarget.style.color = 'var(--rose)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--hair2)'; e.currentTarget.style.color = 'var(--faint)'; }}
        >
          Delete node
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          <p style={{ fontSize: 13, color: 'var(--inkS)', margin: 0, lineHeight: 1.5 }}>
            Delete {node.label}? This removes it from the graph.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setConfirmDelete(false)}
              style={{
                flex: 1, padding: '8px 16px', border: '1px solid var(--hair2)',
                borderRadius: 8, background: 'none', color: 'var(--inkS)',
                fontFamily: "'DM Sans', sans-serif", fontSize: 13, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{
                flex: 1, padding: '8px 16px', border: 'none', borderRadius: 8,
                background: 'var(--rose)', color: 'white', fontFamily: "'DM Sans', sans-serif",
                fontSize: 13, cursor: deleting ? 'default' : 'pointer', fontWeight: 500,
                opacity: deleting ? 0.6 : 1,
              }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
